import React from "react";

// highchart
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";

// @mui
import styled from "@emotion/styled";

import Typography from "../mui/Typography";
import Stack from "../mui/Stack";
import Box from "../mui/Box";

// common components
import MagicLoadingBox from "../common/MagicLoadingBox";

// interfaces
interface ChartData {
  name: string;
  data: number[];
}

interface PerformanceWiseStatusInfo {
  text: string;
  color: string;
}

interface PerformanceWiseParameterBtnInfo {
  id: number;
  name: string;
}

interface Props {
  isLoading: boolean;
  chartData: ChartData | any;
  performIndicatorsGraphCategories: string[];
  performanceWiseStatusInfo: PerformanceWiseStatusInfo[];
  performanceWiseParameterBtnInfo: PerformanceWiseParameterBtnInfo[];
  activeCentresPerformanceHeaderBtnId: number;
  performanceWiseParametersDataSwitching: (
    activeBtnId: number,
    activeBtnName: string
  ) => void;
}

const PerformanceIndicatorsGraph: React.FC<Props> = ({
  chartData,
  isLoading,
  performanceWiseStatusInfo,
  performanceWiseParameterBtnInfo,
  performIndicatorsGraphCategories,
  activeCentresPerformanceHeaderBtnId,
  performanceWiseParametersDataSwitching,
}) => {
  // handle loading stats of data
  if (isLoading) return <MagicLoadingBox height={500} />;

  const options: Highcharts.Options = {
    chart: {
      type: "column",
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: performIndicatorsGraphCategories,
      labels: {
        useHTML: true,
        formatter: function () {
          const value = this.value;
          if (typeof value === "string") {
            return `
                  <span 
                      class="custom-label" 
                      data-full="${value}" 
                      onmouseover="showFullName(this)" 
                      onmouseout="hideFullName(this)">
                      ${value.length > 20 ? value.slice(0, 20) + "..." : value}
                  </span>`;
          }
          return `<span class="custom-label">${value}</span>`;
        },
      },
    },

    yAxis: {
      min: 0,
      max: 100,
      title: {
        text: "",
      },
      tickInterval: 20,
      minPadding: 0.1,
      maxPadding: 0.1,
    },

    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        if (!this.points) return ""; // Ensure points exist
    
        let tooltip = `<b>${this.x} - Youth Enrolment %</b><br/>`;
    
        this.points.forEach((point:any) => {
          tooltip += `
            <span style="color:${point.color}">●</span> 
            <span style="color:${point.color}; font-weight: bold;">${point.series.name}:</span> 
            <b>${point.y}</b> (${point.point.options.percentage}%)<br/>
          `;
        });
    
        return tooltip;
      },
    },

    legend: {
      enabled: false,
    },
    credits: {
      enabled: false, // Hides the Highcharts.com logo
    },
    exporting: {
      enabled: false,
    },
    plotOptions: {
      column: {
        stacking: "percent",
        dataLabels: {
          enabled: true,
          format: "{point.percentage:.0f}%",
        },
      },
    },

    series: chartData ? JSON.parse(JSON.stringify(chartData)) : [],
  };

  return (
    <StyledStackContainer>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb="20px"
      >
        <StyledGraphHeading>
          Performance Indicators : Doing well vs Need Attention
        </StyledGraphHeading>

        {/* Legend  */}
        <Stack direction="row" gap="16px" alignItems="center">
          {performanceWiseStatusInfo?.map(
            (info: PerformanceWiseStatusInfo, index: number) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    backgroundColor: info.color,
                    borderRadius: "4px",
                    width: "20px",
                    height: "20px",
                    cursor: "pointer",
                  }}
                />
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: "500",
                    lineHeight: "20px",
                  }}
                >
                  {info.text}
                </p>
              </div>
            )
          )}
        </Stack>

        {/* Line Graph Button */}
        <div style={{ display: "flex", gap: "0px" }}>
          {performanceWiseParameterBtnInfo?.map(
            (btn: PerformanceWiseParameterBtnInfo, index: number) => (
              <button
                key={btn.id}
                onClick={() =>
                  performanceWiseParametersDataSwitching(index, btn.name)
                }
                style={{
                  padding: "10px 16px",
                  backgroundColor:
                    btn.id == activeCentresPerformanceHeaderBtnId
                      ? "#FFF1B7"
                      : "#FFF",
                  color:
                    btn.id === activeCentresPerformanceHeaderBtnId
                      ? "#333"
                      : "#6D6D6D",
                  fontWeight: "600",
                  border:
                    btn.id === activeCentresPerformanceHeaderBtnId
                      ? "1px solid #FFF1B7"
                      : "1px solid #FFF",
                  borderBottom:
                    btn.id === activeCentresPerformanceHeaderBtnId
                      ? "2px solid #333"
                      : "2px solid #D0D5DD",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
              >
                {btn.name}
              </button>
            )
          )}
        </div>
      </Stack>

      <Box sx={{ height: "2px", backgroundColor: "#D7D7D7", mb: "20px" }} />
      {chartData?.[0]?.data.length !== 0 &&
      chartData?.[1]?.data.length !== 0 ? (
        <HighchartsReact highcharts={Highcharts} options={options} />
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "300px",
          }}
        >
          <Typography>No data found</Typography>
        </Box>
      )}
    </StyledStackContainer>
  );
};

const StyledStackContainer = styled(Stack)({
  width: "100%",
  margin: "0 auto",
  border: "1px solid #D7D7D7",
  marginBottom: "24px",
  borderRadius: "16px",
  padding: "20px",
  boxSizing: "border-box",
  overflow: "hidden",
  backgroundColor: "white",
});

const StyledGraphHeading = styled(Typography)({
  fontWeight: 600,
  fontSize: "15px",
  lineHeight: "16.94px",
});

export default PerformanceIndicatorsGraph;
